import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../services/Supabase';
import { toast } from 'react-toastify';

/* React Icons */
import { AiOutlineEye, AiOutlineEyeInvisible, AiOutlineExclamationCircle, AiOutlineClose } from "react-icons/ai";
import { FaSpinner } from 'react-icons/fa';
import CompanyLogo from "../../assets/img/company-logo.webp";
import Loader from '../../components/Loader';

const SignIn = () => {
    const [showErrorMsg, setShowErrorMsg] = useState(false);
    const [showPass, setShowPass] = useState(false);

    /* FORMS: Use States / Setters */
    const [formData, setFormData] = useState({
        email: "",
        password: "",
    });
    const { email, password } = formData;
    
    const navigate = useNavigate();
    const { user, setUser, fetchUserMetaData, setUserMetaData, userMetaData, loading, setLoading } = useAuth();

    /* Use Effects */
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.hash.substring(1));
        const errorObject = {};
        for (const [key, value] of urlParams.entries()) {
            errorObject[key] = value;
        }
        if (errorObject.error && errorObject.error_code && errorObject.error_description) {
            toast.error(errorObject.error_description);
        }
        if (errorObject.message) {
            toast.success(errorObject.message);
        }
    }, []);

    // Redirect if already logged in
    useEffect(() => {
        if (user && userMetaData) {
            const redirectURL = userMetaData.user_role === 'administrator' ? '/admin/dashboard' : '/dashboard';
            navigate(redirectURL);
        }
    }, [user, userMetaData, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setShowErrorMsg('');
        setLoading(true);

        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email: formData.email,
                password: formData.password,
            });

            if (!error) {
                // Fetch user data from users table
                const userMetaData = await fetchUserMetaData(data.user.id);
                
                if (userMetaData) {
                    setUser(data.user);
                    setUserMetaData(userMetaData);
                } else {
                    setUser([]);
                    setUserMetaData([]);
                }
                
                let fname = data.user.user_metadata.first_name ?? formData.email;
                toast.success(`Welcome to ARI, ${fname}!`);
                navigate("/dashboard");
                
                return true;
            } else {
                if(error.message === "Email not confirmed") {
                    toast.info("Please verify your email address before logging in.");
                    const { error } = await supabase.auth.resend({
                        type: 'signup',
                        email: formData.email
                    });
                    if (!error) {
                        toast.success("Verification email resent. Please check your inbox.");
                    }
                }else{
                    toast.error(error.message);
                }
                return false;
            }
        } catch (error) {
            toast.error(error.message);
            console.error("Login error:", error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    function onChange(e){
        setFormData((prevState) => ({
            ...prevState,
            [e.target.id]: e.target.value,
        }));
    }
    
    if (loading) {
        return <Loader />;
    }

    return (
        <>
            <section className="main-form-section">
                <div className="container">
                    <div className="banner-wrapper">
                        <div className="form-section-wrapper">
                            <form onSubmit={handleSubmit}>
                                <div className="form-header"><img src={CompanyLogo} alt="Company Logo" /></div>
                                {/* Show if error occured */}
                                {showErrorMsg ? (
                                    <div className="alert alert-error">
                                        <div className="alert-content">
                                            <div className="icon__wrapper">
                                                <span><AiOutlineExclamationCircle /></span>
                                            </div>
                                            <p>The email or password you entered is incorrect.</p>
                                        </div>
                                        <span className="close" onClick={()=>setShowErrorMsg(false)}><AiOutlineClose /></span>
                                    </div>
                                ) : ''}

                                <div className="login-heading">
                                    <h2>Login to ARI</h2>
                                </div>
                                {/* Email */}
                                <div className="form-wrapper">
                                    <label htmlFor="email">Email Address *</label>
                                    <div className="form-group">
                                        <input type="email" className="form-input" id="email" name="email" placeholder="Email Address" value={email} onChange={onChange} required />
                                    </div>
                                </div>
                                {/* Password */}
                                <div className="form-wrapper">
                                    <label htmlFor="password">Password *</label>
                                    <div className="form-group">
                                        <input type={showPass ? "text" : "password"} className="form-input" id="password" name="password" placeholder="Password" value={password} onChange={onChange} required />
                                        <i className="show-icon">{showPass ? <AiOutlineEyeInvisible onClick={()=>setShowPass((prevState) => !prevState)} /> : <AiOutlineEye onClick={()=>setShowPass((prevState) => !prevState)} />}</i>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <div className="remember-me">
                                        <input type="checkbox" value="lsRememberMe" id="rememberMe" /> <label htmlFor="rememberMe">Remember me</label>
                                    </div>
                                </div>
                                <div className="form-group">
                                    {loading ? 
                                        (<button className="submit-button w-100" type="submit"><FaSpinner className="spinner" disabled /> Logging in... </button>) : 
                                        (<button className="submit-button w-100" id="submit-button" type="submit"> Login</button>)
                                    }
                                </div>
                                <div className="sign-in-text">
                                    <ul>
                                        <li><Link to="/forgot-password">Forgot Password</Link></li>
                                    </ul>
                                    <ul className='mt-20'>
                                        <li>Don't have an account? <Link to="/sign-up">Sign Up</Link></li>
                                    </ul>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default SignIn;